import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, MODE_FR, playState } from "../../lib/api";
import { usePatients } from "../../hooks/useData";
import { PhoneFrame } from "../../components/game/PhoneFrame";
import { PauseNotice, ScaleInput, useScales } from "../../components/game/PainFatigue";
import { Logo } from "../../components/brand/Logo";
import { useI18n } from "../../i18n";
import { ReadAloud } from "../../components/brand/Experience";

const Big = ({ children, testId, ...p }) => (
  <button data-testid={testId} {...p}
    className="h-16 w-full rounded-full bg-brand text-lg font-extrabold text-white shadow-lg shadow-[#17A2A2]/30 transition-transform active:scale-[.98] disabled:opacity-40">{children}</button>
);

export default function PatientEntry() {
  const nav = useNavigate();
  const { t } = useI18n();
  const { data: patients } = usePatients();
  const [pid, setPid] = useState(playState.get().patientId);
  const [session, setSession] = useState(undefined);
  const [step, setStep] = useState("disclaimer");
  const [ack, setAck] = useState(false);
  const sc = useScales(0, 0);

  useEffect(() => {
    if (!pid && patients?.length) setPid((patients.find((p) => p.is_demo) || patients[0]).id);
  }, [patients, pid]);
  useEffect(() => { if (pid) api.get(`/sessions/active/${pid}`).then((r) => setSession(r.data)); }, [pid]);

  const patient = patients?.find((p) => p.id === pid);
  const freeSession = async () => {
    const r = await api.post("/sessions", { patient_id: pid, config: { mode: "assisted", duration_min: 15 } });
    setSession(r.data);
    toast.success("Séance libre créée (mode Assisté)");
  };
  const start = async () => {
    await api.post(`/sessions/${session.id}/start`, { pain_before: sc.pain, fatigue_before: sc.fatigue });
    playState.set({ patientId: pid, sessionId: session.id });
    nav("/patient/calibration");
  };
  const resume = () => { playState.set({ patientId: pid, sessionId: session.id }); nav("/patient/map"); };

  return (
    <PhoneFrame className="overflow-y-auto">
      <div className="flex min-h-full flex-col gap-5 p-5 pb-10" style={{ touchAction: "pan-y" }}>
        <div className="flex items-center justify-between">
          <button data-testid="patient-back-home" onClick={() => nav("/")} className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow"><ArrowLeft /></button>
          <Logo size={44} />
        </div>
        <h1 className="font-display text-3xl font-extrabold text-[#0A2540]">Bonjour{patient ? `, ${patient.name}` : ""}</h1>

        {patients?.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {patients.map((p) => (
              <button key={p.id} data-testid={`patient-pick-${p.id}`} onClick={() => setPid(p.id)}
                className={`flex h-12 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-bold ${p.id === pid ? "bg-[#0A2540] text-white" : "bg-white text-[#0A2540]"}`}>
                <UserRound className="h-4 w-4" />{p.name}</button>
            ))}
          </div>
        )}

        {session === null && (
          <div data-testid="no-session-card" className="rounded-3xl bg-white p-5 shadow-sm">
            <p className="text-base font-semibold">Aucune séance prescrite par votre thérapeute.</p>
            <p className="mt-1 text-sm text-slate-500">Vous pouvez demander une séance dans Baobab Care, ou lancer une séance libre en mode Assisté.</p>
            <div className="mt-4"><Big testId="free-session-button" onClick={freeSession}>Séance libre (Assisté)</Big></div>
          </div>
        )}

        {session && (
          <div data-testid="active-session-card" className="rounded-3xl bg-[#0A2540] p-5 text-white">
            <p className="text-xs font-bold tracking-[0.25em] text-[#4FD1C5]">SÉANCE DU JOUR</p>
            <p className="mt-2 font-display text-2xl font-bold">Le Village · {session.config.duration_min} min</p>
            <p className="mt-1 text-sm text-white/70">Mode {MODE_FR[session.config.mode]}</p>
            {session.status === "in_progress" && session.plan && (
              <button data-testid="resume-session-button" onClick={resume} className="mt-4 h-12 rounded-full bg-white/10 px-5 text-sm font-bold">Reprendre la séance en cours</button>
            )}
          </div>
        )}

        {session && step === "disclaimer" && (
          <div className="rounded-3xl bg-white p-5 shadow-sm" data-testid="safety-disclaimer">
            <ShieldCheck className="mb-3 h-8 w-8 text-[#17A2A2]" />
            <p className="text-base leading-relaxed text-[#0A2540]">{t("disclaimer")}</p>
            <div className="mt-3"><ReadAloud testId="disclaimer-read-aloud" text={`${t("disclaimer")} Ne forcez jamais. Arrêtez-vous dès la moindre douleur.`} /></div>
            <p className="mt-3 text-sm text-slate-500">Ne forcez jamais. Arrêtez-vous dès la moindre douleur. Le bouton rouge ARRÊTER est toujours visible.</p>
            <label className="mt-4 flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl bg-[#EAF7F5] p-3 text-base font-semibold">
              <input data-testid="disclaimer-ack-checkbox" type="checkbox" className="h-6 w-6 accent-[#17A2A2]" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              J'ai compris
            </label>
            <div className="mt-4"><Big testId="disclaimer-continue-button" disabled={!ack} onClick={() => setStep("pain")}>Continuer</Big></div>
          </div>
        )}

        {session && step === "pain" && (
          <div className="flex flex-col gap-4">
            <ScaleInput label={t("painToday")} value={sc.pain} onChange={sc.setPain} testId="pain-slider-before" />
            <ScaleInput label={t("fatigueToday")} value={sc.fatigue} onChange={sc.setFatigue} testId="fatigue-slider-before" />
            {sc.pain >= 7 ? <PauseNotice text={t("pauseMsg")} /> : <Big testId="start-adventure-button" onClick={start}>Commencer l'aventure</Big>}
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}
